import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { Box, Flex, HStack, MixpanelEvent, MixpanelEventProperty, useMixpanel, useSecurity } from '@talos/kyoko';
import { memo, useCallback, useEffect } from 'react';
import type { MultilegTabData } from '../../providers/MarketTabs.types';
import { BLOTTER_HEADER_HEIGHT } from '../../styles/dimensions';
import { Delta1SpreadScreen } from './Delta1SpreadScreen';
import { MultilegComboActions } from './MultilegComboActions';
import { MultilegComboDetails } from './MultilegComboDetails';
import { MultilegComboHeader } from './MultilegComboHeader';
import { setCompactOrderBook } from './MultilegComboSlice';
import { MultilegOrderBook } from './MultilegOrderBook';
import { OptionPayout } from './OptionPayout';
import { OptionStrategyScreen } from './OptionStrategyScreen';
import type { MultilegComboType } from './enums';
import { isMultilegOptionComboType } from './enums';
import { useMultilegTabState, useRowEnricher } from './hooks';
import { MultiComboWrapper } from './styles';

interface MultilegComboProps {
  instrumentType: MultilegComboType;
  resolvedSymbol: string | undefined;
  legSymbols?: string[];
  legMarketAccounts?: string[];
  legMarkets?: string[];
  initiatingLegs: boolean[] | undefined;
  haircutRatios: string[] | undefined;
  compactOrderBook: boolean | undefined;
  panelID: string;
  updateMultilegTabData: (tabData: MultilegTabData, label: string) => void;
}

export const MultilegCombo = memo(
  ({
    instrumentType,
    compactOrderBook,
    initiatingLegs,
    haircutRatios,
    resolvedSymbol: symbol,
    legSymbols,
    legMarketAccounts,
    legMarkets,
    panelID,
    updateMultilegTabData,
  }: MultilegComboProps) => {
    const mixpanel = useMixpanel();
    useMultilegTabState({
      instrumentType,
      compactOrderBook,
      initiatingLegs,
      haircutRatios,
      symbol,
      legSymbols,
      legMarketAccounts,
      legMarkets,
      panelID,
    });
    useRowEnricher({ tag: 'MultilegCombo', panelID });

    const instrumentField = useAppStateSelector(
      (state: AppState) => state.multilegCombo.panels[panelID]?.instrumentField
    );
    const resolvedSymbol = useAppStateSelector(
      (state: AppState) => state.multilegCombo.panels[panelID]?.resolvedSymbol
    );
    const security = useSecurity(resolvedSymbol);

    useEffect(() => {
      if (security) {
        mixpanel.track(MixpanelEvent.ViewDeepDive, { [MixpanelEventProperty.ProductType]: security.ProductType });
      }
    }, [mixpanel, security]);

    const dispatch = useAppStateDispatch();
    const handleCompactClick = useCallback(
      (compactOrderBook: boolean | undefined) => {
        dispatch(setCompactOrderBook({ compactOrderBook: !compactOrderBook, panelID }));
      },
      [dispatch, panelID]
    );

    return (
      <MultiComboWrapper>
        <Box p="spacing8" background="backgroundContent">
          <MultilegComboHeader
            symbol={resolvedSymbol}
            instrumentType={instrumentField?.value?.value}
            panelID={panelID}
            legMarketAccounts={legMarketAccounts}
            legMarkets={legMarkets}
          />
        </Box>

        <HStack justifyContent="space-between" mb={1} background="colors.gray.020" gap="spacing12" p="spacing8">
          <MultilegComboDetails security={security} panelID={panelID} />
        </HStack>

        {isMultilegOptionComboType(instrumentField?.value?.value) ? (
          <OptionStrategyScreen panelID={panelID} />
        ) : (
          <Delta1SpreadScreen panelID={panelID} />
        )}

        <MultilegComboActions panelID={panelID} updateMultilegTabData={updateMultilegTabData} />

        {isMultilegOptionComboType(instrumentField?.value?.value) ? (
          <Flex justifyContent="flex-end" overflow="hidden" flex="1" borderTop="solid 1px" borderColor="backgroundBody">
            <Flex
              flexDirection="column"
              flex="1 1 67%"
              gap="1px"
              overflow="hidden"
              borderRight="solid 1px"
              borderColor="backgroundBody"
            >
              <Box p="spacing12" h={BLOTTER_HEADER_HEIGHT} background="backgroundContent" color="colorTextImportant">
                Probability Chart
              </Box>
              <OptionPayout panelID={panelID} />
            </Flex>
            {resolvedSymbol && (
              <MultilegOrderBook
                symbol={resolvedSymbol}
                compactOrderBook={compactOrderBook}
                onCompactClick={handleCompactClick}
              />
            )}
          </Flex>
        ) : (
          <HStack justifyContent="flex-end" h="100%" borderTop="solid 1px" borderColor="backgroundBody">
            {resolvedSymbol && (
              <MultilegOrderBook
                symbol={resolvedSymbol}
                compactOrderBook={compactOrderBook}
                onCompactClick={handleCompactClick}
              />
            )}
          </HStack>
        )}
      </MultiComboWrapper>
    );
  }
);
